import {
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import { Settings } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { findWidgetByName, getImageUrl } from "../../utils";
import { changeShopTheme, getThemeList } from "../../utils/requests";

// Define types for API responses and theme data
interface Theme {
	id: number;
	name: string;
	description: string;
	banner: string;
	shopId: string;
	shopType: string;
	pages: any;
	menus: any;
	widgets: any;
	status: string;
	sortOrder: any;
	createdAt: string;
	updatedAt: string;
	deletedAt: null | string;
}

interface ThemeResponse {
	data: Theme[];
	perPage: number;
	currentPage: number;
	totalPage: number;
	totalResult: number;
}

interface ThemeUpdateOptions {
	themeId: number;
	isPageUpdate: boolean;
	isMenuUpdate: boolean;
	isWidgetUpdate: boolean;
}

const ThemesPage: React.FC = () => {
	// State management
	const [themes, setThemes] = useState<Theme[]>([]);
	const [themeSettingsW, setThemeSettingsW] = useState<any | null>(null);

	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [openModal, setOpenModal] = useState(false);
	const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);
	const [updateOptions, setUpdateOptions] = useState<ThemeUpdateOptions>({
		themeId: 0,
		isPageUpdate: false,
		isMenuUpdate: false,
		isWidgetUpdate: true,
	});
	const [updating, setUpdating] = useState(false);

	const observer = useRef<IntersectionObserver | null>(null);

	// Last element ref for infinite scrolling
	const lastThemeElementRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (loading) return;
			if (observer.current) observer.current.disconnect();

			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasMore) {
					loadMoreThemes();
				}
			});

			if (node) observer.current.observe(node);
		},
		[loading, hasMore]
	);

	// Fetch themes from API
	const fetchThemes = async (page: number, isInitial: boolean = false) => {
		try {
			setLoading(true);
			const response = await getThemeList({
				perPage: 10,
				currentPage: page,
				status: "active",
			});

			const data: Theme[] = response.data as any;

			if (isInitial) {
				setThemes(data);
			} else {
				setThemes((prevThemes) => [...prevThemes, ...data]);
			}

			setHasMore(response.currentPage < response.totalPage);
			setLoading(false);
		} catch (error) {
			console.error("Error fetching themes:", error);
			setLoading(false);
		}
	};

	// Load more themes for infinite pagination
	const loadMoreThemes = () => {
		if (!loading && hasMore) {
			const nextPage = currentPage + 1;
			setCurrentPage(nextPage);
			fetchThemes(nextPage);
		}
	};

	// Initial load
	useEffect(() => {
		fetchThemes(1, true);

		findWidgetByName("Theme Settings").then((res) => {
			setThemeSettingsW(res);
		});
	}, []);

	// Handle theme selection
	const handleSelectTheme = (theme: Theme) => {
		setSelectedTheme(theme);
		setUpdateOptions({
			themeId: theme.id,
			isPageUpdate: false,
			isMenuUpdate: false,
			isWidgetUpdate: true,
		});
		setOpenModal(true);
	};

	// Handle update options change
	const handleOptionChange = (
		option: keyof Omit<ThemeUpdateOptions, "themeId">
	) => {
		setUpdateOptions((prev) => ({
			...prev,
			[option]: !prev[option],
		}));
	};

	// Navigate to theme settings
	const handleNavigateToSettings = () => {
		// router.push('/theme-settings');
	};

	// Apply theme changes
	const handleApplyTheme = async () => {
		if (!selectedTheme) return;

		try {
			setUpdating(true);
			const response = await changeShopTheme(updateOptions);
			// const response = await fetch('/api/v2/shop/theme', {
			//     method: 'POST',
			//     headers: {
			//         'Content-Type': 'application/json',
			//     },
			//     body: JSON.stringify(updateOptions),
			// });

			if (response) {
				// Success message or action
				alert(`Theme "${selectedTheme.name}" applied successfully!`);
				setOpenModal(false);
			} else {
				// Error message
				alert("Failed to apply theme. Please try again.");
			}
		} catch (error) {
			console.error("Error applying theme:", error);
			alert("An error occurred while applying the theme.");
		} finally {
			setUpdating(false);
		}
	};

	return (
		<div className="mx-auto p-4">
			<div className="flex justify-between items-center mb-6">
				<Typography variant="h4" component="h1">
					Theme Gallery
				</Typography>
				<Tooltip title="Theme Settings">
					<Link to={`/widgets/${themeSettingsW?._id}`}>
						<IconButton
							color="primary"
							// onClick={handleNavigateToSettings}
							className="bg-blue-50 hover:bg-blue-100">
							<Settings />
						</IconButton>
					</Link>
				</Tooltip>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
				{themes.map((theme, index) => {
					const isLastElement = index === themes.length - 1;
					return (
						<div
							key={theme.id}
							ref={isLastElement ? lastThemeElementRef : null}>
							<Card
								className="h-full flex flex-col hover:shadow-lg transition-shadow duration-300"
								elevation={3}>
								<CardMedia
									component="img"
									height="140"
									image={
										theme.banner.startsWith("http")
											? theme.banner
											: getImageUrl(theme.banner)
									}
									alt={theme.name}
									className="h-48 object-cover"
									onError={(e) => {
										(e.target as HTMLImageElement).src =
											"https://via.placeholder.com/400x200?text=Theme+Preview";
									}}
								/>
								<CardContent className="flex-grow">
									<Typography
										gutterBottom
										variant="h5"
										component="div">
										{theme.name}
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										className="mb-3">
										{theme.description ||
											"No description available"}
									</Typography>
									<Typography
										variant="caption"
										display="block"
										className="text-gray-500 mb-3">
										Last updated:{" "}
										{new Date(
											theme.updatedAt
										).toLocaleDateString()}
									</Typography>
								</CardContent>
								<Box p={2} pt={0}>
									<Button
										variant="contained"
										color="primary"
										fullWidth
										onClick={() => handleSelectTheme(theme)}
										className="bg-blue-600 hover:bg-blue-700">
										Select Theme
									</Button>
								</Box>
							</Card>
						</div>
					);
				})}
			</div>

			{loading && (
				<div className="flex justify-center my-6">
					<CircularProgress />
				</div>
			)}

			{!hasMore && themes.length > 0 && (
				<Typography className="text-center py-6 text-gray-500">
					You've reached the end of the list
				</Typography>
			)}

			{/* Theme Selection Modal */}
			<Dialog
				open={openModal}
				onClose={() => !updating && setOpenModal(false)}
				maxWidth="sm"
				fullWidth>
				<DialogTitle>
					{selectedTheme
						? `Apply Theme: ${selectedTheme.name}`
						: "Apply Theme"}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" className="mb-4">
						Select which elements you want to update from this
						theme:
					</Typography>

					<div className="space-y-3">
						<FormControlLabel
							control={
								<Checkbox
									checked={updateOptions.isWidgetUpdate}
									onChange={() =>
										handleOptionChange("isWidgetUpdate")
									}
									color="primary"
								/>
							}
							label={
								<div>
									<Typography variant="subtitle2">
										Widgets
									</Typography>
									<Typography
										variant="caption"
										className="text-gray-500">
										Import widgets and their configurations
										from this theme.
									</Typography>
								</div>
							}
							className="items-start"
						/>

						<FormControlLabel
							control={
								<Checkbox
									checked={updateOptions.isPageUpdate}
									onChange={() =>
										handleOptionChange("isPageUpdate")
									}
									color="primary"
								/>
							}
							label={
								<div>
									<Typography variant="subtitle2">
										Pages
									</Typography>
									<Typography
										variant="caption"
										className="text-gray-500">
										Import all pages from this theme
										including layout and content.
									</Typography>

									<div className="mt-1 text-red-500 text-xs">
										** Warning: This will replace all of
										your current home page designs. **
									</div>
								</div>
							}
							className="items-start"
						/>

						<FormControlLabel
							control={
								<Checkbox
									checked={updateOptions.isMenuUpdate}
									onChange={() =>
										handleOptionChange("isMenuUpdate")
									}
									color="primary"
								/>
							}
							label={
								<div>
									<Typography variant="subtitle2">
										Menus
									</Typography>
									<Typography
										variant="caption"
										className="text-gray-500">
										Import navigation menus and their
										structure from this theme.
									</Typography>
								</div>
							}
							className="items-start"
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => !updating && setOpenModal(false)}
						color="inherit"
						disabled={updating}>
						Cancel
					</Button>
					<Button
						onClick={handleApplyTheme}
						color="primary"
						variant="contained"
						disabled={
							updating ||
							(!updateOptions.isPageUpdate &&
								!updateOptions.isMenuUpdate &&
								!updateOptions.isWidgetUpdate)
						}
						className="bg-blue-600 hover:bg-blue-700">
						{updating ? (
							<CircularProgress size={24} />
						) : (
							"Apply Theme"
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default ThemesPage;
